import { createContext, FC, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';

type FavoriteOverrides = Map<number, boolean>;

interface FavoriteChannelOverridesContextValue {
  getIsFavorite: (channelId: number, defaultValue: boolean) => boolean;
  setFavoriteOverride: (channelId: number, isFavorite: boolean) => void;
}

const FavoriteChannelOverridesContext = createContext<FavoriteChannelOverridesContextValue | undefined>(undefined);

export const FavoriteChannelOverridesProvider: FC<PropsWithChildren> = ({ children }) => {
  const [overrides, setOverrides] = useState<FavoriteOverrides>(new Map());

  const getIsFavorite = useCallback(
    (channelId: number, defaultValue: boolean): boolean => {
      return overrides.has(channelId) ? overrides.get(channelId)! : defaultValue;
    },
    [overrides]
  );

  const setFavoriteOverride = useCallback((channelId: number, isFavorite: boolean) => {
    setOverrides(prev => {
      const next = new Map(prev);
      next.set(channelId, isFavorite);
      return next;
    });
  }, []);

  const value = useMemo(() => ({ getIsFavorite, setFavoriteOverride }), [getIsFavorite, setFavoriteOverride]);

  return <FavoriteChannelOverridesContext.Provider value={value}>{children}</FavoriteChannelOverridesContext.Provider>;
};

export const useFavoriteChannelOverrides = () => useContext(FavoriteChannelOverridesContext);
